// src/com/auto_dida_info/AutoDidaInfoInput.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { Button, DialogContent, DialogActions, Grid, CircularProgress } from '@mui/material';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmDateField from '../../common/VzmDateField';
import { ErrorHintBox, VzmPrint } from '../../common/VzmStyled';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { AutoDidaInfoDictionary } from './AutoDidaInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import { arriveInfoListForSettingStoreDidaRepo } from '../../value/AppModel';
import { LIST_ARRIVE_INFO_STORE_LIST_FOR_DIDA } from '../arrive_info/ArriveInfoGql';
import ValueMisc from '../../value/ValueMisc';
import FlexyTable from '../../common/flexytable/FlexyTable';
import SearchBox from '../../common/SearchBox';
import { ArriveInfoDictionary } from '../arrive_info/ArriveInfoDict';

const InputMap = AutoDidaInfoDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * (AUTO_DIDA_INFO) 정보 생성 또는 편집 Dialog
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-18 15:25:50 using DbdbTap
 */
export default function AutoDidaInfoInput({
    open,
    dc,
    item,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const [storeName, setStoreName] = useState('');
    const arriveInfoListForSettingStore = useReactiveVar(arriveInfoListForSettingStoreDidaRepo);
    const [searchText, setSearchText] = useState(''); // search for new store.
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    // ##### Call GraphQL to get List #####
    const [getArriveInfoStoreList, responseStoreList] = useLazyQuery(LIST_ARRIVE_INFO_STORE_LIST_FOR_DIDA, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetArriveInfoStoreList(data, option)},
		onError: (error) => addAlert({title: '점포 목록 조회 오류', message: [error.message]})
    });

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    useEffect(()=>{
        if(open && dc) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                inputSet.data.oldDidaDate = item.didaDate;
                editing = true;
            }
            else {
                const diff =  new Date().getTime()/1000 - arriveInfoListForSettingStore.when;
                if(diff > 600) getArriveInfoStoreList({variables:{dcCd:dc.dcCd}});
                inputSet.data = {...defaultInputData};
                inputSet.data.dcCd = dc.dcCd;
                inputSet.data.didaDate = ValueMisc.getYMD();
                inputSet.error.storeId = true;
                inputSet.errorLabels = [ArriveInfoDictionary.inputMap.storeId.label];
                inputSet.hasError = true;
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item, dc]);

    // >>>>>>>> callbacks <<<<<<<<
    const onCompleteGetArriveInfoStoreList = (data, option) => {
        if(data.arriveInfoStoreListForDida) {
            arriveInfoListForSettingStoreDidaRepo({when:new Date().getTime()/1000, records:data.arriveInfoStoreListForDida});
        }
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        // 편집일 경우 날짜만 변경되어야 하므로 날짜가 다시 원래대로 돌아갔는지 확인해서 변경여부를 추가 설정한다.
        if(isEdit && id === InputMap.didaDate.id) {
            newInput.changed = newInput.data.oldDidaDate !== newInput.data.didaDate;
        }
        setInput(newInput);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    const loadingAny = loadingSave || responseStoreList.loading;

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            title={isEdit ? '점포 휴무일 수정' : '점포 휴무일 등록'}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <VzmPrint
                            label="물류센터"
                            value={dc.dcName}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {
                            isEdit ?
                            <VzmPrint
                                label="점포"
                                value={item ? item.storeName : ''}
                            /> :
                            <>
                            <Box style={{display:'flex', alignItems:'center', marginBottom:5}}>
                                <div style={{
                                    flexGrow:1,
                                    color: AppStyle.PrimaryRGB,
                                    fontSize:'0.8rem'}}
                                >
                                    점포 선택 *
                                    {input.data.storeId && ` (${storeName})`}
                                </div>
                                <SearchBox
                                    value={searchText}
                                    onChangeText={(text)=>setSearchText(text)}
                                    width={200}
                                />
                            </Box>
                            <Box style={{
                                display: 'flex',
                                minWidth: 450,
                                height: 300
                            }}>
                                <FlexyTable
                                    name={ArriveInfoDictionary.tableProps.tableId}
                                    tableProps={ArriveInfoDictionary.storesForDidaTableProps}
                                    records={arriveInfoListForSettingStore.records}
                                    search={searchText}
                                    tools={[]}
                                    onRowClicked={(row)=>{
                                        if(input.data.storeId !== row.storeId) {
                                            onChangeValue(InputMap.storeId.id, row.storeId, false);
                                            setStoreName(row.storeName);
                                        }
                                    }}
                                    backgroundSelected='#f9d7fa'
                                />
                            </Box>
                            </>
                        }
                    </Grid>
                    {
                        isEdit &&
                        <Grid item xs={12}>
                            <VzmPrint
                                label="기존 휴무일"
                                value={input.data.oldDidaDate}
                            />
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <VzmDateField
                            dict={InputMap.didaDate}
                            value={input.data.didaDate}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed || loadingAny}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
